/**
 * bridge.ts — API client for the Bridge Config / Health resource.
 *
 * WEB-353. P10-мост: настройки приложения (App ID, Store URL, MMP,
 * price→level map, test mode) и health-probe. WEB-1450: entitlementMode снесён
 * (право всегда выдаёт наш сервер; сохранённые значения сервер игнорирует).
 * WEB-375: GET /bridge-config response now includes platformTier so the FE can
 * render the Pro-tier locked-state without an extra round-trip.
 *
 * Route prefix: /projects/:projectId/bridge-config | bridge-health |
 * bridge-connection-check (WEB-3978, bridge.write + Pro+)
 * RBAC: GET — PROJECT_READ; PATCH — owner/admin + Pro+ tier (403 иначе).
 */
// WEB-1906 (ревизия пачки 18): путь через алиас `@/api/client`, как у
// большинства модулей этой папки (billing, funnels, leads, uploads…). Не
// косметика: jest подменяет клиент по ТОЧНОМУ имени `@/api/client`
// (moduleNameMapper), потому что настоящий читает `import.meta.env`, который
// jest не парсит. При относительном './client' подмена не срабатывала, и
// любой набор, чей граф импорта задевал этот модуль, падал ещё на разборе —
// ровно это и случилось, когда адреса магазинов понадобились канвасу
// конструктора (16 наборов PhonePreview разом).
import { api } from '@/api/client';
import type { components } from '@/api/generated/schema';

// ─── DTOs ────────────────────────────────────────────────────────────────────

export interface BridgeAppId {
  ios?: string;
  android?: string;
  androidSha256?: string;
}

export interface BridgeStoreUrl {
  ios?: string;
  android?: string;
}

export type MmpProvider = 'appsflyer' | 'adjust' | 'branch';

/**
 * WEB-519: persisted App Connection method discriminator (mirrors backend
 * bridge-config.schema.ts CONNECTION_METHODS). Исторически метод деривился из
 * снесённого entitlementMode (WEB-1450), не различавшего full_bridge и
 * api_only — this field can.
 */
export type BridgeConnectionMethod = 'full_bridge' | 'api_only' | 'test' | 'none';

/** Subscription tier values mirroring the BE SubscriptionTier enum (WEB-375). */
export type PlatformTier = 'FREE' | 'PRO';

export interface BridgeConfig {
  appId?: BridgeAppId;
  storeUrl?: BridgeStoreUrl;
  /**
   * WEB-1450 (п.4): `null` — явный сигнал «сбросить трекер в „не выбран"» на
   * PATCH (partial-merge: пропуск ключа означал бы «не менять»). Зеркало
   * mmpBaseUrl (WEB-728 clear-fix).
   */
  mmp?: MmpProvider | null;
  /**
   * WEB-728: per-project MMP base URL (AppsFlyer OneLink / Adjust base). Feeds
   * the iOS install-link build on the BE. Absent/null → iOS falls back to the
   * email-delivered magic-link. `null` is the explicit «clear» signal on PATCH
   * (empty string would fail the BE `.url()` check).
   */
  mmpBaseUrl?: string | null;
  /**
   * WEB-800: per-project custom URL-scheme возврата в приложение (напр. "myapp",
   * без "://"). Из неё BE собирает готовый `appSchemeUrl` в success-data —
   * альтернативный путь возврата помимо Universal Link. WEB-803: задаётся из UI.
   * WEB-1334 п.3: null — схема стёрта (кабинет шлёт null при очистке поля).
   */
  returnScheme?: string | null;
  /**
   * WEB-1152 (П9): пейволл, служащий порталом отмены с удержанием. Из него BE
   * (PortalLinkService) строит manage_link и ссылку входа по почте. null =
   * портал не назначен, ссылка не выдаётся.
   */
  portalPaywallId?: string | null;
  /** WEB-519: persisted method choice — see BridgeConnectionMethod. */
  connectionMethod?: BridgeConnectionMethod;
  /**
   * WEB-1161: адрес опубликованного веб-пейвола проекта (на домене клиента).
   * Его читает серверная ручка пользователей `POST /s2s/v1/users` и отдаёт
   * приложению клиента готовой ссылкой с дописанными `?guid=&email=`. Поле было
   * живым на бэке с WEB-546, но в кабинет выведено только сейчас — до этого
   * задавалось лишь сырым API-запросом. `null` — явная очистка на PATCH
   * (пустую строку BE-схема `.url()` отвергла бы), зеркало mmpBaseUrl.
   */
  webPaywallUrl?: string | null;
  priceLevelMap?: Record<string, string>;
  testMode?: boolean;
  /**
   * WEB-1213 (в кабинет выведено WEB-1151): опознание посетителя web→app по
   * отпечатку устройства. Гейт на сервере двусторонний — `false` гасит и
   * запись слепка на публичной странице, и опознание из приложения
   * (HandoffFingerprintService).
   *
   * ⚠ Отсутствие поля сервер читает как ВКЛЮЧЕНО (legacy-проекты без
   * миграции) — фронт обязан читать его так же, иначе экран показал бы
   * «выключено» там, где сбор идёт.
   *
   * ⚠ Умолчание «включено» — решение владельца 25.08 и НЕ окончательное:
   * финализируется по цифрам счётчика способа совпадения (matchMethod,
   * миграция 175), когда они наберутся. Менять только новым решением
   * владельца.
   */
  fingerprintMatching?: boolean;
  /**
   * WEB-375: included in the GET response only.
   * The FE uses this to render the Pro-tier locked-state (FREE → locked).
   * Absent on old API responses — component treats absent as NOT locked.
   */
  platformTier?: PlatformTier;
  /**
   * WEB-2129 п.1: отправка вебхуков приостановлена тарифом и момент, с
   * которого она встала (конец льготного окна после понижения).
   *
   * Только в ответе GET. Признак едет ИМЕННО этой ручкой, а не списком
   * получателей вебхуков: список закрыт тарифным гейтом и на FREE отвечает
   * 403 — объяснить молчание было бы негде. Ручка настроек моста открыта
   * любому участнику проекта.
   *
   * Отсутствие полей (старый ответ API) кабинет читает как «не знаем» и
   * плашку не показывает: объявить остановку, которой нет, хуже молчания.
   * `bridgeWorkPausedSince: null` при `bridgeWorkPaused: true` — «дату
   * назвать нечем», а не «остановлено сейчас».
   */
  bridgeWorkPaused?: boolean;
  bridgeWorkPausedSince?: string | null;
}

export interface BridgeHealth {
  status: 'connected' | 'never';
  lastCallbackAt: string | null;
}

/**
 * WEB-3978: итог «Проверить связь» — пять строк в порядке эталона
 * (`app_reached`, `link_files`, `fingerprint`, `google`, `apple`) с машинными
 * `status` (ok/err/wait/none) и `reason`, время проверки и последний сигнал.
 * Тип — из собранного генератором контракта, своей копии формы нет.
 */
export type BridgeConnectionCheck =
  components['schemas']['BridgeConnectionCheckResponseDto'];

// ─── API object ───────────────────────────────────────────────────────────────

export const bridgeApi = {
  /**
   * GET /projects/:projectId/bridge-config
   * Returns BridgeConfig or {} when not yet configured.
   */
  getConfig: (projectId: string) =>
    api.get<BridgeConfig>(`/projects/${projectId}/bridge-config`),

  /**
   * PATCH /projects/:projectId/bridge-config
   * Merges partial BridgeConfig — returns merged BridgeConfig.
   * 403 for non-owner/admin.
   */
  updateConfig: (projectId: string, data: Partial<BridgeConfig>) =>
    api.patch<BridgeConfig>(`/projects/${projectId}/bridge-config`, data),

  /**
   * GET /projects/:projectId/bridge-health
   * Returns { status: 'connected'|'never', lastCallbackAt: string|null }.
   */
  getHealth: (projectId: string) =>
    api.get<BridgeHealth>(`/projects/${projectId}/bridge-health`),

  /**
   * GET /projects/:projectId/bridge-connection-check — WEB-3978.
   * «Проверить связь»: owner/admin + Pro+ (403 `CAPABILITY_REQUIRED` /
   * `PRO_REQUIRED` иначе). В отличие от соседей отдаёт само тело ответа.
   */
  checkConnection: (projectId: string): Promise<BridgeConnectionCheck> =>
    api
      .get<BridgeConnectionCheck>(
        `/projects/${projectId}/bridge-connection-check`,
      )
      .then((res) => res.data),
};
